import { useState } from "react";
import { bool, func, object, oneOf, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { MaxTimeModifiers } from "@repo/utils";
import { getFirstWorkdayOfMonth, getFirstWorkdayOfWeek } from "@repo/structure";
import { useEstimateStructure } from "@tools";
import { Button, ClearInput, Dialog } from "@components";
import { Grid, IconButton, Slider } from "@material-ui/core";
import { SyncAltRounded } from "@material-ui/icons";
import useStyles from "./TransferWorkload.style.js";
import classnames from "classnames";

const PERIOD_RANK = { day: 1, week: 2, month: 3 };

const getPeriodStartDate = (period, periodType) => {
  if (period.type === periodType) return period.startDate;
  if (periodType === "month") return getFirstWorkdayOfMonth(period.startDate);
  if (periodType === "week") return getFirstWorkdayOfWeek(period.startDate);
  return period.startDate;
};

const getLabel = (element, period) =>
  period ? `${period.type} · ${period.startDate.toLocaleDateString()}` : element.name;

export const TransferWorkload = observer(({
  workType,
  source,
  target,
  period,
  periodStart,
  sourcePeriod,
  targetPeriod,
  placement = "right",
  size = "large",
  onTransferred,
  offset,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const classes = useStyles({ isLarge: size === "large" });

  const sourceRank = PERIOD_RANK[sourcePeriod?.type] || 0;
  const targetRank = PERIOD_RANK[targetPeriod?.type] || 0;
  const periodType = sourceRank >= targetRank ? sourcePeriod?.type : targetPeriod?.type;
  const sourcePeriodValue = sourcePeriod?.type === periodType
    ? sourcePeriod?.value
    : sourcePeriod?.[`${periodType}Value`] ?? sourcePeriod?.value;
  const targetPeriodValue = targetPeriod?.type === periodType
    ? targetPeriod?.value
    : targetPeriod?.[`${periodType}Value`] ?? targetPeriod?.value;
  const sourceValue = (sourcePeriod ? sourcePeriodValue : source.workload ?? 0) / MaxTimeModifiers.HOUR;
  const targetValue = (targetPeriod ? targetPeriodValue : target.workload ?? 0) / MaxTimeModifiers.HOUR;
  const total = sourceValue + targetValue;
  const isPeriodTransfer = Boolean(sourcePeriod && targetPeriod);
  const transferSourcePeriod = sourcePeriod && {
    ...sourcePeriod,
    type: periodType,
    startDate: getPeriodStartDate(sourcePeriod, periodType),
  };
  const transferTargetPeriod = targetPeriod && {
    ...targetPeriod,
    type: periodType,
    startDate: getPeriodStartDate(targetPeriod, periodType),
  };

  const setSourceValue = (value) => {
    setSliderValue(Math.max(0, Math.min(total, Number(value) || 0)));
  };
  const setTargetValue = (value) => {
    setSliderValue(total - Math.max(0, Math.min(total, Number(value) || 0)));
  };

  const [ open, setOpen ] = useState(false);
  const [ sliderValue, setSliderValue ] = useState(sourceValue);

  const close = () => {
    setOpen(false);
    setSliderValue(sourceValue);
  };

  const transfer = () => {
    const delta = Math.abs(sliderValue - sourceValue);
    if (!delta) return;
    structure.historyManager.startGroup();
    try {
      const reversed = sliderValue > sourceValue;
      if (isPeriodTransfer) {
        const from = reversed ? transferTargetPeriod : transferSourcePeriod;
        const to = reversed ? transferSourcePeriod : transferTargetPeriod;
        source.transferWorkloadBetweenPeriods(delta * MaxTimeModifiers.HOUR, periodType, from.startDate, to.startDate);
      } else {
        const from = reversed ? target : source;
        const to = reversed ? source : target;
        workType.transferTeamMemberWorkload(from.id, to.id, delta * MaxTimeModifiers.HOUR, period, periodStart);
      }
      onTransferred?.();
      close();
    } finally {
      structure.historyManager.stopGroup();
    }
  };

  return (
    <>
      <span className={classnames(
        classes.host,
        placement,
      )}>
        <IconButton
          size="small"
          color="primary"
          onClick={(event) => {
            event.stopPropagation();
            setSliderValue(sourceValue);
            setOpen(true);
          }}
          aria-label={t("views.editor.dialogs.timeline_settings.transfer_workload")}
          className={classnames(
            "noDrag p-0-5",
            classes.trigger,
            size === "large" ? "text-md" : "text-sm",
            offset && "offset",
            placement,
          )}
        >
          <SyncAltRounded className={classnames(["top", "bottom"].includes(placement) && "rotate-90")} />
        </IconButton>
      </span>
      <Dialog
        open={open}
        onClose={close}
        maxWidth="xs"
        fullWidth
        title={t("views.editor.dialogs.timeline_settings.transfer_workload")}
        actions={<>
          <Button
            variant="outlined"
            onClick={close}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={sliderValue === sourceValue}
            onClick={transfer}
          >
            {t("views.editor.dialogs.timeline_settings.apply_transfer", "Transfer")}
          </Button>
        </>}
      >
        <Grid container direction="column" alignItems="center">
          <Grid item container justifyContent="space-between">
            <Grid item xs>
              <p>{getLabel(source, transferSourcePeriod)}</p>
              <ClearInput
                value={sliderValue}
                onChange={setSourceValue}
                min={0}
                max={total}
                showDecimals
                suffix="h"
                fullWidth
              />
            </Grid>
            <Grid item xs>
              <p>{getLabel(target, transferTargetPeriod)}</p>
              <ClearInput
                value={total - sliderValue}
                onChange={setTargetValue}
                min={0}
                max={total}
                showDecimals
                suffix="h"
                fullWidth
              />
            </Grid>
          </Grid>
          <Slider
            value={parseFloat((sliderValue).toFixed(2))}
            min={0}
            max={total}
            step={1}
            onChange={(_, next) => setSliderValue(next)}
            valueLabelDisplay="auto"
            aria-label={t("views.editor.dialogs.timeline_settings.transfer_workload")}
            className="w-full"
          />
        </Grid>
      </Dialog>
    </>
  );
});

TransferWorkload.propTypes = {
  workType: object,
  source: object.isRequired,
  target: object.isRequired,
  period: string,
  periodStart: object,
  sourcePeriod: object,
  targetPeriod: object,
  placement: oneOf(["top", "bottom", "left", "right"]),
  onTransferred: func,
  offset: bool,
  size: oneOf(["small", "large"]),
};
